import { ColDef } from 'ag-grid-enterprise';
import { VChip } from 'vuetify/lib';
import { alignCenter, alignLeft, alignRight, comparator, component } from './utils';
import { h } from 'vue';
import { formatPrettyNumber } from '@/modules/common/components/pretty-number';
import {
  CustomDataType,
  formatPbRateDetails,
  formatSideAdapter,
  priceWithPinnedAgg,
  rateAdapter,
  userIdentifier,
} from './common';
import { loanEvents, newLoanEvents } from '@/utils/api/loans';
import * as cols from '@/modules/common/components/ag-table/columns/common';
import { aggSelector } from './open-loans';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import { Benchmark } from '@/connect/gen/consts/benchmarkconsts_pb';
import { Benchmark as OldBenchmark } from '@/utils/api/loans';
import { SettlementType } from '@/connect/gen/consts/settlementconsts_pb';
import { ContractHistoryEvent } from '@/connect/gen/modules/apiengine/services/contracts/contracts_pb';
import LoanHistoryTableActions from '@/modules/open-loans/components/LoanHistoryTableActions.vue';
import { LoanEventType } from '@/modules/common/models';
import { PbRateDetails } from '@/connect/gen/common/protos/types_pb';
import { formatAnyDate } from '@/utils/helpers/dates';
import { useCompaniesStore } from '@/store/companies-store';
import { formatCompanyInfo } from '@/modules/agreements/utils';
import { ContractHistoryEventType } from '@/connect/gen/consts/loanhistoryconsts_pb';
import { SelectValues } from '@/types/select';
import {
  filterBigInt,
  filterDate,
  filterDecimal,
  filterSelect,
  filterSelectMultiple,
  filterText,
} from './filter';
export { checkbox } from './common';

const eventTypeAdapter = component<{
  eventType: LoanEventType;
}>(
  (props) => () =>
    h(
      VChip,
      {
        props: { color: loanEvents[props.eventType].color, xSmall: true },
        style: { minWidth: '8rem', justifyContent: 'center' },
      },
      loanEvents[props.eventType].label.toLocaleUpperCase()
    )
);

function getEventTypeFilterValues(): SelectValues<ContractHistoryEventType> {
  return Object.entries(newLoanEvents).map(([key, label]) => ({
    text: loanEvents[label].label,
    value: Number(key) as ContractHistoryEventType,
  }));
}

export function eventType(config?: { filter?: boolean }): ColDef {
  return {
    field: 'eventType',
    colId: 'eventType',
    headerName: 'Event Type',
    valueFormatter: (params) =>
      params.node?.rowPinned
        ? params.data && 'nounOrQuantity' in params.data
          ? params.data.nounOrQuantity('eventTypes')
          : undefined
        : newLoanEvents[params.value as LoanEventType],
    cellRendererSelector: (params) => {
      if (
        params.node?.rowPinned &&
        params.data &&
        'noun' in params.data &&
        params.data.noun?.eventTypes.length === 1
      ) {
        return eventTypeAdapter({
          eventType: newLoanEvents[params.data.noun.eventTypes[0]],
        });
      }
      if (!params.node?.rowPinned) {
        return eventTypeAdapter({
          eventType: newLoanEvents[params.value],
        });
      }
    },
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: getEventTypeFilterValues(),
      })),
  };
}

export function eventInitiator(config?: { filter?: boolean }): ColDef {
  return {
    field: 'initiator',
    colId: 'initiator',
    headerName: 'Initiator',
    ...userIdentifier(),
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
  };
}

export function side(config?: { filter?: boolean }): ColDef {
  return {
    ...cols.side(),
    field: 'contractState.side',
    sortable: true,
    valueFormatter: (params) =>
      params.node?.rowPinned
        ? params.node?.rowPinned && params.data && 'nounOrQuantity' in params.data
          ? params.data.nounOrQuantity('sides')
          : undefined
        : Side[params.value],
    cellRendererSelector: (params) => {
      if (
        params.node?.rowPinned &&
        params.data &&
        'noun' in params.data &&
        params.data.noun?.sides.length === 1
      ) {
        return formatSideAdapter({
          side: params.data.noun.sides[0],
        });
      }
      if (!params.node?.rowPinned) {
        return formatSideAdapter({
          side: params.value === Side.BORROW ? 'BORROW' : 'LOAN',
        });
      }
    },
    ...(config?.filter &&
      filterSelect([
        { text: 'All', value: null },
        { text: 'Lender', value: Side.LOAN },
        { text: 'Borrower', value: Side.BORROW },
      ])),
  };
}

export const eventTime = (config?: { filter?: boolean }): ColDef => ({
  ...cols.timestamp({
    field: 'eventTime',
    headerName: 'When',
  }),
  ...(config?.filter && filterDate()),
});

export function openQuantity(config?: { filter?: boolean }): ColDef {
  return {
    field: 'contractState.openQuantity',
    colId: 'contractState.openQuantity',
    headerName: 'Open',
    valueFormatter: (params) =>
      formatPrettyNumber(
        params.node?.rowPinned && params.data
          ? params.data[params.data.selected].openQuantity
          : params.value
      ),
    useValueFormatterForExport: false,
    ...alignRight(),
    ...(config?.filter && filterBigInt({ allowNegative: false })),
  };
}

export function openQuantityChange(config?: { filter?: boolean }): ColDef {
  return {
    field: 'contractState.openQuantityChange',
    colId: 'contractState.openQuantityChange',
    headerName: 'Quantity Change',
    valueFormatter: (params) =>
      formatPrettyNumber(
        params.node?.rowPinned && params.data
          ? params.data[params.data.selected].openQuantity
          : params.value
      ),
    useValueFormatterForExport: false,
    ...alignRight(),
    ...(config?.filter && filterBigInt({ allowNegative: true })),
  };
}

export function unitPrice(config?: { filter?: boolean }): ColDef {
  return {
    sortable: false,
    ...priceWithPinnedAgg({
      field: 'contractState.unitPrice',
      colId: 'contractState.unitPrice',
      headerName: 'Unit Price',
      pinnedValueGetter: (params) => params.data[params.data.selected].unitPrice,
    }),
    ...(config?.filter && filterDecimal({ allowNegative: false })),
  };
}

export function rate(config?: {
  filter?: boolean;
}):
  | ContractHistoryEvent
  | (ContractHistoryEvent & { avg: PbRateDetails & { rateModifiers: Benchmark[] } }) extends infer D
  ? ColDef<D, Pull<D, 'contractState.rateDetails'>>
  : never {
  return {
    field: 'contractState.rateDetails',
    colId: 'contractState.rateDetails',
    headerName: 'Rebate Rate',
    comparator: (a, b) => comparator.decimal(a?.rate, b?.rate),
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
    valueFormatter: (params) =>
      params.node?.rowPinned ? '' : !params.value ? '' : formatPbRateDetails(params.value),
    cellRendererSelector: (params) => {
      if (
        params.node?.rowPinned &&
        params.data &&
        'avg' in params.data &&
        params.data.avg.rateModifiers.length === 1
      ) {
        const rateModifier = Benchmark[params.data.avg.rateModifiers[0]] as OldBenchmark;
        return rateAdapter({
          rate: params.data.avg.rate,
          rateModifier: rateModifier || undefined,
          precision: undefined,
        });
      }
      if (!params.node?.rowPinned && params.value?.benchmark !== undefined) {
        const rateModifier = Benchmark[params.value.benchmark] as OldBenchmark;
        return rateAdapter({
          rate: params.value.rate,
          rateModifier: rateModifier || undefined,
          precision: undefined,
        });
      }
    },
    ...(config?.filter && filterDecimal({ allowNegative: true })),
  };
}

export function settlementAmount(config?: { filter?: boolean }): ColDef {
  return {
    ...priceWithPinnedAgg({
      field: 'contractState.contractValue',
      colId: 'contractState.contractValue',
      headerName: 'Contract Value',
      pinnedValueGetter: (params) => params.data[params.data.selected].contractValue,
    }),
    ...(config?.filter && filterDecimal({ allowNegative: false })),
  };
}

export function settlementAmountChange(config?: { filter?: boolean }): ColDef {
  return {
    ...priceWithPinnedAgg({
      field: 'contractState.contractValueAdjustment',
      colId: 'contractState.contractValueAdjustment',
      headerName: 'Contract Value Adj',
      pinnedValueGetter: (params) => params.data[params.data.selected].contractValueAdjustment,
    }),
    ...(config?.filter && filterDecimal({ allowNegative: true })),
  };
}

export function rateAmount(config?: { filter?: boolean }): ColDef {
  return {
    ...priceWithPinnedAgg({
      field: 'contractState.interestDueAmount',
      colId: 'contractState.interestDueAmount',
      headerName: 'Rate Amount',
      pinnedValueGetter: (params) => params.data[params.data.selected].interestDueAmount,
    }),
    ...(config?.filter && filterDecimal({ allowNegative: true })),
  };
}

function getCounterpartyFilterValues(): SelectValues<string> {
  const companiesStore = useCompaniesStore();
  return companiesStore.companies.map((company) => ({
    text: formatCompanyInfo(company),
    value: company.displayId,
  }));
}

export function counterparty(config?: { filter?: boolean }): ColDef {
  return {
    field: 'contractState.counterpartyId',
    colId: 'contractState.counterpartyId',
    headerName: 'Counterparty',
    valueFormatter: (params) => {
      if (params.node?.rowPinned) {
        return params.data.nounOrQuantity('parties');
      }

      if (!params.value) {
        return '';
      }

      const company = useCompaniesStore().getCompany(params.value);
      return company === null ? '' : formatCompanyInfo(company);
    },
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: getCounterpartyFilterValues(),
      })),
  };
}

export function settlementType(config?: { filter?: boolean }): ColDef {
  return {
    headerName: 'Sett Type',
    field: 'contractState.settlementType',
    colId: 'contractState.settlementType',
    valueFormatter: (params) => {
      if (params.node?.rowPinned) return '';
      return SettlementType[params.value];
    },
    // @TODO: fix agg
    // valueFormatter: (params) => {
    //   const value = proto3
    //     .getEnumType(SettlementType)
    //     .values.find((v) => v.no === params.value)!.name;
    //   return params.node?.rowPinned ? params.data.nounOrQuantity('settlementType') : value;
    // },
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: [
          { text: 'NSCC', value: SettlementType.NSCC },
          { text: 'Bilateral', value: SettlementType.BILATERAL },
          { text: 'OCC', value: SettlementType.OCC },
        ],
      })),
  };
}

export function ticker(config?: { filter?: boolean }): ColDef {
  return {
    ...cols.ticker(),
    field: 'contractState.instrument.ticker',
    colId: 'contractState.instrument.ticker',
    valueFormatter: (params) =>
      params.node?.rowPinned ? params.data.nounOrQuantity('ticker') : params.value,
    ...(config?.filter && filterText({ trim: true, uppercase: true, mode: 'contains' })),
  };
}

export function cusip(config?: { filter?: boolean }): ColDef {
  return {
    field: 'contractState.instrument.cusip',
    colId: 'contractState.instrument.cusip',
    cellDataType: CustomDataType.CUSIP,
    headerName: 'CUSIP',
    valueFormatter: (params) =>
      params.node?.rowPinned ? params.data.nounOrQuantity('cusip') : params.value,
    ...alignLeft(),
    ...(config?.filter && filterText({ trim: true, uppercase: true, mode: 'contains' })),
  };
}

export function displayId(config?: { filter?: boolean }): ColDef {
  return {
    field: 'contractState.id',
    colId: 'contractState.id',
    headerName: 'Contract ID',
    sortable: false,
    ...alignLeft(),
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
  };
}

export function settlementDate(config?: { filter?: boolean }): ColDef {
  return {
    field: 'contractState.settlementDate',
    colId: 'contractState.settlementDate',
    headerName: 'Sett Date',
    valueFormatter: (params) => {
      if (params.node?.rowPinned) return '';
      return formatAnyDate(params.value, 'dd MMM');
    },
    ...alignLeft(),
    ...(config?.filter && filterDate()),
  };
}

export function marketValue(config?: { filter?: boolean }): ColDef {
  return {
    ...priceWithPinnedAgg({
      field: 'contractState.marketValue',
      colId: 'contractState.marketValue',
      headerName: 'Market Value',
      pinnedValueGetter: (params) => params.data[params.data.selected].marketValue,
    }),
    ...(config?.filter && filterDecimal({ allowNegative: false })),
  };
}

const actionsAdapter = component<{
  event: ContractHistoryEvent;
  viewLoan: (loanId: string) => void;
}>(
  (props) => () =>
    h(LoanHistoryTableActions, {
      on: { 'view-loan': () => props.viewLoan(props.event.contractState!.auroraInternalId!) },
    })
);

export function actions({
  changeSumOrAvg,
  viewLoan,
}: {
  changeSumOrAvg: (sumOrAvg: 'sum' | 'avg') => void;
  viewLoan: (loanId: string) => void;
}): ColDef {
  return {
    field: 'actions',
    colId: 'actions',
    headerName: 'Actions',
    sortable: false,
    cellRendererSelector: (params) =>
      params.node?.rowPinned
        ? aggSelector({
            sumOrAvg: params.data.selected,
            // @TODO: this should probably be an optional prop?
            showDropdownActions: undefined,
            changeSumOrAvg,
          })
        : actionsAdapter({ event: params.data, viewLoan }),
    pinned: 'right',
    lockVisible: true,
    width: 100,
    maxWidth: 100,
    ...alignCenter(),
  };
}
